import React from 'react';
import { Col, Container, Form, Row, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import Button from '../../../Parts/Button';
import Money from '../../../Parts/Money';
import PowerShadesTypeahead from '../../../Parts/PowerShadesTypeahead';
import apiCalls from '../../../PowerShadesAPIFunctions';
import { UsePortalDispatch } from '../../../Store';
import { addQuoteAwning, setAwningsUnlocked, updateQuoteAwning } from '../../../Store/entities/awnings';
import { useQuoteAwning } from '../../../Store/entities/awnings/hooks';
import { useNextQuoteLineNumber } from '../../../Store/entities/lineNumbers';
import { useCurrentMultiplier } from '../../../Store/entities/multipliers/hooks';
import { useQuoteById } from '../../../Store/entities/quotes/hooks';
import { isFailedApiCall, toTitleCase } from '../../../psUtil';
import {
	AWNING_DISPLAY_NAME,
	AWNING_PART_NUMBER,
	type AwningConfigurationRequest,
	type AwningConfigurationResponse,
	type AwningFabric,
	type AwningProperty,
} from '../../awningTypes';
import {
	MOUNTING_OPTION_PROPERTY_NUMBER,
	REMOTE_PROPERTY_NUMBER,
	SENSOR_PROPERTY_NUMBER,
	applyMountingDefaults,
	buildInitialConfiguration,
	clearBracketSelectionsOnMountingChange,
	configurationToApiPayload,
	configurationToQuoteParameters,
	ensureRequiredOptionValues,
	getConfigFieldForProperty,
	getPitchOptions,
	getRequiredOptionValues,
	isConfigurationComplete,
	isMultiSelectPropertyNumber,
	normalizeAwningConfigureResponse,
	normalizeAwningFabrics,
	normalizeAwningProperties,
	parametersToConfiguration,
	shouldShowAwningFormProperty,
	getAwningConfigureTierPrice,
	getAwningPriceTierFromMultiplierName,
} from '../../awningConfiguratorUtils';

type NewAwningFormProps = {
	quoteId: number;
	quoteType: string;
	onCancel: () => void;
	editLineNumber?: number;
};

const NewAwningForm = ({ quoteId, quoteType, onCancel, editLineNumber }: NewAwningFormProps) => {
	const dispatch = UsePortalDispatch();
	const existingAwning = useQuoteAwning(quoteId, editLineNumber ?? -1);
	const isEditMode = Boolean(editLineNumber);
	const quote = useQuoteById(quoteId);
	const isQuoteEditable = quote?.editable ?? false;
	const isUnlocked = Boolean(existingAwning?.unlocked && isQuoteEditable);
	const fieldsDisabled = isEditMode && !isUnlocked;
	const multiplier = useCurrentMultiplier(quoteId);

	const [properties, setProperties] = React.useState<AwningProperty[]>([]);
	const [fabrics, setFabrics] = React.useState<AwningFabric[]>([]);
	const [config, setConfig] = React.useState<AwningConfigurationRequest | null>(null);
	const [selectedFabricCode, setSelectedFabricCode] = React.useState('');
	const [roomName, setRoomName] = React.useState('');
	const [shadeName, setShadeName] = React.useState('');
	const [configureResult, setConfigureResult] = React.useState<AwningConfigurationResponse | null>(null);
	const [configureErrors, setConfigureErrors] = React.useState<string[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [configuring, setConfiguring] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);
	const [loadError, setLoadError] = React.useState<string | null>(null);

	React.useEffect(() => {
		const load = async () => {
			setLoading(true);
			setLoadError(null);
			try {
				const [optionsResp, fabricsResp] = await Promise.all([
					apiCalls.getAwningOptions(),
					apiCalls.getAwningFabrics(),
				]);

				if (isFailedApiCall(optionsResp) || isFailedApiCall(fabricsResp)) {
					throw new Error('Unable to load awning configuration data.');
				}

				const loadedProperties = normalizeAwningProperties(optionsResp.data);
				const loadedFabrics = normalizeAwningFabrics(fabricsResp.data);
				if (loadedProperties.length === 0 || loadedFabrics.length === 0) {
					throw new Error('Awning options or fabrics were not returned by the server.');
				}

				setProperties(loadedProperties);
				setFabrics(loadedFabrics);

				if (isEditMode && existingAwning?.parameters?.length) {
					const initialConfig = parametersToConfiguration(existingAwning.parameters);
					// Awnings quoted before the remote became a multi-select may hold a single legacy
					// value; make sure the required remote is present so the API accepts the reprice.
					initialConfig.remote = ensureRequiredOptionValues(
						loadedProperties.find((p) => p.property_number === REMOTE_PROPERTY_NUMBER),
						initialConfig.remote,
					);
					applyMountingDefaults(initialConfig);
					setConfig(initialConfig);
					setSelectedFabricCode(initialConfig.fabric_number);
					setRoomName(existingAwning.room_name ?? '');
					setShadeName(existingAwning.shade_name ?? '');
				} else {
					const initialFabric = loadedFabrics[0] ?? null;
					const initialConfig = buildInitialConfiguration(loadedProperties, initialFabric);
					setConfig(initialConfig);
					setSelectedFabricCode(initialFabric?.ke_fabric_code ?? '');
				}
			} catch (err: any) {
				setLoadError(err?.message ?? 'Failed to load awning options.');
			} finally {
				setLoading(false);
			}
		};

		load();
	}, [editLineNumber, existingAwning, isEditMode]);

	const selectedFabric = React.useMemo(
		() => (Array.isArray(fabrics)
			? fabrics.find((f) => f.ke_fabric_code === selectedFabricCode)
			: null) ?? null,
		[fabrics, selectedFabricCode],
	);

	const fabricOptions = React.useMemo(
		() => fabrics.map((fabric) => ({
			label: fabric.color_name,
			value: fabric.ke_fabric_code,
		})),
		[fabrics],
	);

	const selectedFabricOption = React.useMemo(() => {
		if (!selectedFabric) return [];
		return [{ label: selectedFabric.color_name, value: selectedFabric.ke_fabric_code }];
	}, [selectedFabric]);

	const mountingProperty = React.useMemo(
		() => properties.find((p) => p.property_number === MOUNTING_OPTION_PROPERTY_NUMBER),
		[properties],
	);

	const runConfigure = React.useCallback(async (nextConfig: AwningConfigurationRequest) => {
		if (!isConfigurationComplete(nextConfig)) {
			setConfigureResult(null);
			setConfigureErrors([]);
			return;
		}

		setConfiguring(true);
		try {
			const resp = await apiCalls.configureAwning(configurationToApiPayload(nextConfig));
			if (isFailedApiCall(resp)) {
				throw resp.error;
			}
			const normalized = normalizeAwningConfigureResponse(resp.data);
			setConfigureResult(normalized);
			setConfigureErrors(normalized.errors ?? []);
		} catch (err: any) {
			setConfigureResult(null);
			setConfigureErrors([err?.message ?? 'Unable to configure awning.']);
		} finally {
			setConfiguring(false);
		}
	}, []);

	React.useEffect(() => {
		if (!config) return undefined;
		const timer = window.setTimeout(() => {
			runConfigure(config);
		}, 400);
		return () => window.clearTimeout(timer);
	}, [config, runConfigure]);

	const updateConfig = (updates: Partial<AwningConfigurationRequest>) => {
		setConfig((current) => {
			if (!current) return current;
			const mountingChanged = 'mounting_option' in updates
				&& updates.mounting_option !== current.mounting_option;
			const next = { ...current, ...updates };

			if (mountingChanged) {
				clearBracketSelectionsOnMountingChange(next, mountingProperty, current.mounting_option);
			}

			applyMountingDefaults(next, { autoFillBrackets: false });
			return { ...next };
		});
	};

	const updateProperty = (property: AwningProperty, value: string) => {
		const field = getConfigFieldForProperty(property.property_number);
		if (!field || !config) return;
		updateConfig({ [field]: value } as Partial<AwningConfigurationRequest>);
	};

	const handleFabricChange = (fabricCode: string) => {
		setSelectedFabricCode(fabricCode);
		const fabric = Array.isArray(fabrics)
			? fabrics.find((f) => f.ke_fabric_code === fabricCode)
			: undefined;
		if (fabric) {
			updateConfig({ fabric_brand: fabric.fabric_brand, fabric_number: fabric.ke_fabric_code });
		}
	};

	const handleFabricClear = () => {
		setSelectedFabricCode('');
		updateConfig({ fabric_brand: '', fabric_number: '' });
	};

	// Accessories and awnings share one line number sequence on the backend, so this has to be
	// derived from both collections -- see selectNextQuoteLineNumber.
	const nextLineNumber = useNextQuoteLineNumber(quoteId);

	const [submitError, setSubmitError] = React.useState<string | null>(null);

	const handleUnlock = () => {
		if (!editLineNumber) return;
		dispatch(setAwningsUnlocked({
			quoteId,
			lineNumbers: [editLineNumber],
			unlocked: true,
		}));
	};

	const handleSaveAwning = async () => {
		if (!config || !configureResult?.is_valid || fieldsDisabled) return;

		setSubmitting(true);
		setSubmitError(null);
		try {
			const parms = configurationToQuoteParameters(config, selectedFabric?.color_name);
			if (isEditMode && editLineNumber) {
				await dispatch(updateQuoteAwning({
					quoteId,
					lineNumber: editLineNumber,
					parms,
					room_name: roomName,
					shade_name: shadeName,
				})).unwrap();
			} else {
				await dispatch(addQuoteAwning({
					quoteId,
					sku: AWNING_PART_NUMBER,
					parms,
					nextLineNumber,
					room_name: roomName,
					shade_name: shadeName,
				})).unwrap();
			}
			onCancel();
		} catch (err: any) {
			setSubmitError(err?.message ?? `Unable to ${isEditMode ? 'update' : 'add'} awning.`);
			console.error(err);
		} finally {
			setSubmitting(false);
		}
	};

	const handleDone = async () => {
		if (fieldsDisabled) {
			onCancel();
			return;
		}
		if (config && configureResult?.is_valid && !configuring && !submitting) {
			await handleSaveAwning();
			return;
		}
		onCancel();
	};

	const previewPrice = getAwningConfigureTierPrice(
		configureResult,
		getAwningPriceTierFromMultiplierName(multiplier.name),
	);
	const pricedUnit = previewPrice * (multiplier.currency_multiplier ?? 1);
	const hasConfigureErrors = configureErrors.length > 0
		|| (!configuring && configureResult !== null && !configureResult.is_valid);
	const configureErrorMessage = configureErrors.length > 0
		? configureErrors.join('; ')
		: 'Invalid awning configuration.';

	if (loading) {
		return (
			<Container className="local-bootstrap py-4 text-center">
				<Spinner animation="border" />
				<div className="mt-2">Loading awning options...</div>
			</Container>
		);
	}

	if (loadError || !config) {
		return (
			<Container className="local-bootstrap py-3">
				<Alert variant="danger">{loadError ?? 'Unable to load awning form.'}</Alert>
				<Button color="gray" onClick={onCancel}>Close</Button>
			</Container>
		);
	}

	const sensorProperty = properties.find((p) => p.property_number === SENSOR_PROPERTY_NUMBER);
	const sensorOptions = sensorProperty?.options.map((option) => option.value) ?? [];

	const remoteProperty = properties.find((p) => p.property_number === REMOTE_PROPERTY_NUMBER);
	const remoteOptions = remoteProperty?.options.map((option) => option.value) ?? [];
	const requiredRemotes = getRequiredOptionValues(remoteProperty);
	const remoteHelpText = requiredRemotes.length > 0
		? `${requiredRemotes.map(toTitleCase).join(', ')} is included with every awning and cannot be removed.`
		: '';

	return (
		<Container className="local-bootstrap">
			<Row>
				<Col>
					<h2 className={`text-${quoteType} m-0`}>
						{isEditMode ? `Edit` : `New`}
						{' '}
						Awning
					</h2>
				</Col>
			</Row>

			{!isEditMode && (
				<h4 className={`text-${quoteType}`}>All Fields Required</h4>
			)}

			{submitError && (
				<Alert variant="danger" className="mt-2">
					{submitError}
				</Alert>
			)}

			<Row className="mt-2 g-3 text-start">
				{properties.filter((property) => (
					shouldShowAwningFormProperty(property, mountingProperty, config.mounting_option)
				)).map((property) => {
					// Multi-select properties (remote, sensors) render their own checkbox pickers below.
					if (isMultiSelectPropertyNumber(property.property_number)) return null;
					const field = getConfigFieldForProperty(property.property_number);
					if (!field) return null;

					const options = property.property_number === 17
						? getPitchOptions(config.mounting_option, property).map((o) => o.value)
						: property.options.map((o) => o.value);

					return (
						<Col md={6} key={property.property_number}>
							<Form.Label htmlFor={`awning-${property.property_number}`}>
								{toTitleCase(property.property_name)}
							</Form.Label>
							<Form.Select
								id={`awning-${property.property_number}`}
								className="text-start"
								disabled={fieldsDisabled}
								value={config[field] as string}
								onChange={(e) => updateProperty(property, e.target.value)}
							>
								<option value="">Select...</option>
								{options.map((option) => (
									<option key={option} value={option}>{toTitleCase(option)}</option>
								))}
							</Form.Select>
						</Col>
					);
				})}

				<Col md={12}>
					<Form.Label htmlFor="awning-fabric">Fabric</Form.Label>
					<PowerShadesTypeahead
						id="awning-fabric"
						labelKey="label"
						inputClass="text-start"
						disabled={fieldsDisabled}
						options={fabricOptions}
						selected={selectedFabricOption}
						positionFixed
						placeholder="Select fabric..."
						onChange={(selected) => {
							if (selected.length === 0) {
								handleFabricClear();
								return;
							}
							const option = selected[0] as { label: string, value: string };
							if (option?.value) {
								handleFabricChange(option.value);
							}
						}}
					/>
				</Col>

				{remoteProperty && (
					<Col md={12}>
						<Form.Label htmlFor="awning-remote">{toTitleCase(remoteProperty.property_name || 'Remote')}</Form.Label>
						<PowerShadesTypeahead
							id="awning-remote"
							multiple
							checkboxMenu
							clearButton={false}
							inputClass="text-start"
							disabled={fieldsDisabled}
							positionFixed
							placeholder="Select remotes..."
							options={remoteOptions}
							selected={config.remote}
							onChange={(selected) => {
								updateConfig({
									remote: ensureRequiredOptionValues(remoteProperty, selected as string[]),
								});
							}}
						/>
						{remoteHelpText && (
							<Form.Text className="text-muted">{remoteHelpText}</Form.Text>
						)}
					</Col>
				)}

				{sensorProperty && (
					<Col md={12}>
						<Form.Label htmlFor="awning-sensors">Sensors (optional)</Form.Label>
						<PowerShadesTypeahead
							id="awning-sensors"
							multiple
							checkboxMenu
							clearButton
							inputClass="text-start"
							disabled={fieldsDisabled}
							positionFixed
							placeholder="Select sensors..."
							options={sensorOptions}
							selected={config.sensors}
							onChange={(selected) => {
								updateConfig({ sensors: selected as string[] });
							}}
						/>
					</Col>
				)}

				<Col md={6}>
					<Form.Label htmlFor="awning-room-name">Room Name</Form.Label>
					<Form.Control
						id="awning-room-name"
						className="text-start"
						disabled={fieldsDisabled}
						value={roomName}
						onChange={(e) => setRoomName(e.target.value)}
					/>
				</Col>
				<Col md={6}>
					<Form.Label htmlFor="awning-shade-name">Awning Name</Form.Label>
					<Form.Control
						id="awning-shade-name"
						className="text-start"
						disabled={fieldsDisabled}
						value={shadeName}
						onChange={(e) => setShadeName(e.target.value)}
					/>
				</Col>
			</Row>

			{isEditMode ? (
				<>
					{hasConfigureErrors ? (
						<div className="price-error text-center pt-4">
							<h6 className="text-danger">{configureErrorMessage}</h6>
						</div>
					) : (
						<div className="price-success text-center pt-4">
							<h4 style={{ color: '#8dc63f' }}>
								{configuring ? (
									<Spinner size="sm" animation="border" />
								) : (
									<Money value={pricedUnit} />
								)}
							</h4>
						</div>
					)}
					<Col className="d-flex flex-column align-items-center justify-content-center">
						<Button
							className="col-lg-4"
							onClick={handleDone}
							loading={submitting}
						>
							Done
						</Button>
						{fieldsDisabled && (
							<Button
								className="col-lg-4 mt-1"
								onClick={handleUnlock}
							>
								<FontAwesomeIcon icon={faLock} />
							</Button>
						)}
					</Col>
				</>
			) : (
				<>
					{hasConfigureErrors ? (
						<div className="price-error text-center pt-4 pb-2">
							<h6 className="text-danger">{configureErrorMessage}</h6>
						</div>
					) : configureResult?.is_valid && previewPrice !== 0 ? (
						<div className={`pt-4 pb-2 shade-price text-${quoteType}`}>
							<Money value={pricedUnit} />
						</div>
					) : null}
					<Container className="local-bootstrap">
						<Row className="mt-4 mb-2">
							<Col sm={6} xl={4}>
								<Button fullWidth color="gray" onClick={onCancel}>
									Cancel
								</Button>
							</Col>
							<Col xl={4} className="d-none d-xxl-block d-xl-block" />
							<Col sm={6} xl={4} className="text-end">
								<Button
									fullWidth
									disabled={!configureResult?.is_valid || configuring || submitting}
									color={quoteType}
									onClick={handleSaveAwning}
								>
									{configuring || submitting ? 'Calculating...' : 'Add Awning'}
								</Button>
							</Col>
						</Row>
					</Container>
				</>
			)}
		</Container>
	);
};

export default NewAwningForm;
